import type { AxiosError } from "axios";
import { PromptValue, type RedirectRequest } from "@azure/msal-browser";
import MsalInstance from "src/services/authClient";

/**
 * The challenge's error_description when this session was signed out. Mirrors
 * the backend's RevokedSessionJwtBearerSetup.ErrorDescription.
 */
export const SIGNED_OUT_SESSION_DESCRIPTION = "The session has been signed out";

/**
 * The challenge's error_description when a newer sign-in elsewhere ended this
 * session under the concurrent session cap. Mirrors the backend's
 * RevokedSessionJwtBearerSetup.ReplacedErrorDescription.
 */
export const REPLACED_SESSION_DESCRIPTION =
	"The session was ended by a newer sign-in";

/** Why the app signed the user out, for the sign-in screen to explain. */
export type SignOutReason = "replaced";

const SIGN_OUT_REASON_KEY = "mee.signOutReason";
const FRESH_SIGN_IN_KEY = "mee.freshSignIn";

function challengeOf(error: unknown): string | undefined {
	const response = (error as AxiosError | undefined)?.response;
	if (response?.status !== 401) return undefined;
	const challenge = response.headers?.["www-authenticate"];
	return typeof challenge === "string" ? challenge : undefined;
}

/**
 * True when the API refused the bearer token itself — expired, or from an
 * ended session (the backend's RevokedSessionJwtBearerSetup: signed out, or
 * replaced by a newer sign-in) — rather than an endpoint answering 401 for its
 * own reasons. Only a refused token comes back with the `invalid_token`
 * challenge; a plain `Results.Unauthorized()` (the chunked-upload ownership
 * check, for one) carries no challenge at all.
 */
export function isTokenRejection(error: unknown): boolean {
	return challengeOf(error)?.includes('error="invalid_token"') ?? false;
}

/** True when the refusal says a newer sign-in elsewhere ended this session. */
export function isSessionReplaced(error: unknown): boolean {
	return (
		isTokenRejection(error) &&
		(challengeOf(error)?.includes(REPLACED_SESSION_DESCRIPTION) ?? false)
	);
}

/**
 * True when the refusal says the API has ended this Entra session for good
 * (signed out, or replaced), as opposed to a token that merely expired.
 */
export function isSessionEnded(error: unknown): boolean {
	if (!isTokenRejection(error)) return false;
	const challenge = challengeOf(error) ?? "";
	return (
		challenge.includes(SIGNED_OUT_SESSION_DESCRIPTION) ||
		challenge.includes(REPLACED_SESSION_DESCRIPTION)
	);
}

// Session storage: it goes with the tab, and survives a redirect round trip
// in it. Storage can be unavailable (a locked-down browser); then the courtesy
// it carries is lost, never the sign-out itself.
function store(key: string, value: string): void {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		// See above.
	}
}

function read(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

function forget(key: string): void {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		// Nothing was stored, then.
	}
}

/** The reason the app last signed this tab out, if it recorded one. */
export function readSignOutReason(): SignOutReason | null {
	return read(SIGN_OUT_REASON_KEY) === "replaced" ? "replaced" : null;
}

/**
 * Forgets the reason, once the user signs in again. Separate from the read,
 * which the sign-in screen does on every mount, so a remount can't lose the
 * explanation before the user has read it.
 */
export function clearSignOutReason(): void {
	forget(SIGN_OUT_REASON_KEY);
}

/**
 * Makes this tab's next sign-in a fresh one (`prompt=login`) rather than a
 * silent one. The API refuses an ended session's id for good, so signing in
 * again only works if Entra starts a new session. Silently, Entra would hand
 * back the browser's existing session, whose id is the ended one: it is still
 * alive when Mee signed out locally, or when the user left Entra's own sign-out
 * page without picking an account, and the app would then be refused and
 * signed out again on every attempt.
 */
export function requireFreshSignIn(): void {
	store(FRESH_SIGN_IN_KEY, "1");
}

/**
 * The request for the Sign in button: a fresh sign-in if this tab's last
 * session was ended (see requireFreshSignIn), MSAL's default otherwise.
 * Consumes the flag and the sign-out reason, since the user is now signing in.
 */
export function takeSignInRequest(): RedirectRequest | undefined {
	const fresh = read(FRESH_SIGN_IN_KEY) === "1";
	forget(FRESH_SIGN_IN_KEY);
	clearSignOutReason();
	return fresh ? { scopes: [], prompt: PromptValue.LOGIN } : undefined;
}

let signingOut = false;

/**
 * Signs out once, however many callers ask at the same time. Released again if
 * MSAL refuses (another interaction in progress), so a later request can retry.
 */
function signOutOnce(signOut: () => Promise<void>): Promise<void> {
	if (signingOut) return Promise.resolve();
	signingOut = true;
	return signOut().catch(() => {
		signingOut = false;
	});
}

/** Ends the Entra session too, via Entra's own sign-out page. */
export function signOutOfEntra(): Promise<void> {
	return signOutOnce(() => MsalInstance.logoutRedirect());
}

/**
 * Signs out of Mee only: clears MSAL's cache for this app and shows the
 * sign-in screen, without sending the user to Entra's sign-out page (an
 * account picker that does nothing until they choose, which is confusing
 * straight after being signed out by someone else's sign-in). The browser's
 * Entra session stays as it was, for the other Microsoft apps that use it; Mee
 * refuses its session id anyway, and the next sign-in is a fresh one.
 *
 * `clearCache` rather than `logoutRedirect` with its navigation suppressed:
 * that one marks an interaction in progress and fetches Entra's metadata
 * first, and if the fetch fails the mark stays, refusing the next sign-in.
 * `clearCache` needs no network, but tells msal-react nothing, so the page
 * reloads to come back up signed out.
 */
function signOutOfMee(): Promise<void> {
	return signOutOnce(async () => {
		await MsalInstance.clearCache();
		// MSAL logs and swallows a failure to clear. Reloading with the account
		// still cached would be refused again, and reload again, for ever: fall
		// back to Entra's sign-out instead.
		if (MsalInstance.getAllAccounts().length > 0) {
			await MsalInstance.logoutRedirect();
			return;
		}
		window.location.reload();
	});
}

/**
 * Response-interceptor hook: when the API refuses the token, sign out rather
 * than fetch another token, since a token from the same Entra session would
 * carry the same refused session id.
 * - An ended session (signed out, or replaced by a newer sign-in): sign out of
 *   Mee only, and make the next sign-in a fresh one, so it can't land back in
 *   the ended session.
 * - Anything else (an expired token): sign out fully, through Entra.
 * Skipped in the mobile app's web view, whose tokens and sign-in belong to the
 * app.
 */
export function handleTokenRejection(error: unknown): void {
	if (!isTokenRejection(error) || window.ReactNativeWebView) return;
	if (!isSessionEnded(error)) {
		void signOutOfEntra();
		return;
	}
	if (isSessionReplaced(error)) store(SIGN_OUT_REASON_KEY, "replaced");
	requireFreshSignIn();
	void signOutOfMee();
}
